/*
 * The remembered grouping preference, shared by Upcoming and Past.
 *
 * §10: the toggle's "choice remembered per user". §11 uses "the same control and
 * color rule". Contract §2 puts the memory on the SERVER — "group-by memory
 * (fencer/tournament per view) ... cross-device consistency requires these on the
 * server, not local storage" — and the two views remember SEPARATELY, which is why
 * there are two columns and why this hook takes which view it is for.
 *
 * WHY THE OPTIMISTIC UPDATE IS THE POINT. A toggle that waits for a round trip
 * before moving is a toggle that feels broken on venue wifi, and the regroup it
 * triggers is pure client-side work over data already in hand — there is nothing
 * to wait for. So the local value changes immediately and the PUT follows.
 *
 * AND WHY IT REVERTS ON FAILURE. The alternative is a control showing a state the
 * server does not hold, which reappears wrong on the next device. Reverting is the
 * honest failure: the toggle springs back, which is visible, rather than lying
 * quietly.
 */

import { useCallback, useState } from "react";

import { api, type User } from "./api";
import type { GroupBy } from "../components/SegmentedToggle";

export type GroupedView = "upcoming" | "past";

export function useGroupBy(
  view: GroupedView,
  user: User,
  onUserChanged: (user: User) => void,
): [GroupBy, (next: GroupBy) => void] {
  const serverValue = view === "upcoming" ? user.upcoming_group_by : user.past_group_by;
  const [value, setValue] = useState<GroupBy>(serverValue);

  const change = useCallback(
    (next: GroupBy) => {
      const previous = value;
      setValue(next);

      // The WHOLE preference object, because the endpoint is a PUT: sending only
      // the changed field would reset the others to their zero values.
      void api
        .preferences({
          theme: user.theme,
          reduce_motion: user.reduce_motion,
          upcoming_group_by: view === "upcoming" ? next : user.upcoming_group_by,
          past_group_by: view === "past" ? next : user.past_group_by,
          // Round-tripped unchanged, and BOTH have to be here. The compiler
          // catches a missing `notification_name_format` because it is
          // required; it cannot catch a missing `ui_locale`, which is optional
          // on the wire precisely because null is a value — so omitting it
          // would quietly reset somebody's language when they flipped a
          // grouping toggle (decisions 535, 537).
          notification_name_format: user.notification_name_format,
          ...(user.ui_locale ? { ui_locale: user.ui_locale } : {}),
          ...(user.persona_hint
            ? { persona_hint: user.persona_hint as "fencer" | "parent" | "coach" }
            : {}),
        })
        .then((body) => onUserChanged(body.user as User))
        .catch(() => setValue(previous));
    },
    [value, view, user, onUserChanged],
  );

  return [value, change];
}
